import { Hex, TransactionReceipt } from 'viem';

import type { ParsedSwap } from '@/lib/0x-parser';
import type {
  CrossChainFailureContext,
  CrossChainStatusResponse,
  CrossChainStep,
} from '@/lib/api/0x-cross-chain/types';

import { Fee, SolanaSwapProvider } from '@/utils/0x.types';
import type { ExecutionPlan } from '@/utils/executionPlan';
import { ChainHash, SolanaHash } from '@/utils/models';
import { TokenInfo } from '@/utils/token.types';

import type {
  GaslessStatusResponse,
  GaslessSubmitResponse,
  GaslessSubmitReceipt,
} from '../../utils/gasless/types';
import type {
  IntentsStatusResponse,
  IntentsSubmitReceipt,
  IntentsSubmitResponse,
} from '../../utils/intents/types';

export type SerializableTransactionReceipt = Omit<TransactionReceipt<string>, 'logs' | 'logsBloom'>;

export type NotObservedReceipt = {
  status: 'not_observed';
  detectedAt: string;
};

/**
 * Receipt committed when a pending transaction is cancelled or replaced in the
 * user's wallet (same nonce, different transaction). The original swap did not
 * execute, so this is a terminal failure rather than a success. `repriced`
 * (sped-up) transactions are NOT represented here -- they are the same logical
 * swap, so the tracked hash is updated in place and the normal receipt path runs.
 */
export type ReplacedReceipt = {
  status: 'replaced';
  reason: 'cancelled' | 'replaced';
  detectedAt: string;
  /** Hash of the replacing transaction that landed on-chain. */
  replacedByHash: ChainHash;
};

/**
 * Partial receipt built from the EIP-5792 wallet-call status, committed when the
 * full receipt fetch fails. It carries the bundle's real terminal status;
 * `source` discriminates it from a full receipt, whose statuses overlap.
 */
export type WalletCallFallbackReceipt = {
  status: 'success' | 'reverted';
  source: 'wallet_call';
  detectedAt: string;
  transactionHash?: Hex;
  blockHash?: Hex;
  blockNumber?: string;
  gasUsed?: string;
};

export type WrapTransaction = TransactionCommonFields & {
  raw?: SerializableTransactionReceipt | NotObservedReceipt | ReplacedReceipt;
  transactionType: 'wrap';
};

export type SwapTransaction = TransactionCommonFields & {
  raw?: SerializableTransactionReceipt | NotObservedReceipt | ReplacedReceipt;
  isBatched?: boolean;
  transactionType: 'swap';
};

export type BatchSwapTransaction = TransactionCommonFields & {
  raw?:
    | SerializableTransactionReceipt
    | NotObservedReceipt
    | ReplacedReceipt
    | WalletCallFallbackReceipt;
  transactionType: 'batch-swap';
};

export type SolanaSwapTransaction = TransactionCommonFields & {
  raw?: undefined;
  transactionType: 'solana-swap';
  hash: SolanaHash;
  status?: 'confirmed' | 'failed';
  /**
   * Which provider won the quote race for this trade. Read by the updater-driven
   * terminal analytics; undefined while the race flag is off.
   */
  provider?: SolanaSwapProvider;
};

export type GaslessTransaction = TransactionCommonFields & {
  raw?: GaslessSubmitResponse | GaslessStatusResponse;
  transactionType: 'gasless';
};

export type IntentsTransaction = TransactionCommonFields & {
  // The submit response initially; IntentsWatcher then merges each status
  // snapshot over it, producing the IntentsSubmitReceipt shape.
  raw?: IntentsSubmitResponse | IntentsSubmitReceipt | IntentsStatusResponse;
  transactionType: 'intents';
};

export type CrossChainStatusReceipt = {
  status: 'success' | 'reverted';
};

export type CrossChainTransaction = TransactionCommonFields & {
  raw?: CrossChainStatusReceipt;
  transactionType: 'cross-chain';
  serviceTime: number;
  maxServiceTime: number;
  /** Route steps from the submitted quote. Used to distinguish bridge-only from swap+bridge UX. */
  quoteSteps?: CrossChainStep[];
  quoteId: string;
  /** Latest status response from the 0x cross-chain status API. */
  tradeStatus?: CrossChainStatusResponse;
  /** Source chain transaction hash */
  sourceTxHash?: string;
  /** Destination chain transaction hash (populated when bridge_filled) */
  destinationTxHash?: string;
  /** Destination chain ID */
  destinationChainId?: number;
  /** Failure context from the 0x API (populated when status is bridge_failed) */
  failureContext?: CrossChainFailureContext;
};

export type Transaction =
  | WrapTransaction
  | SwapTransaction
  | BatchSwapTransaction
  | SolanaSwapTransaction
  | GaslessTransaction
  | IntentsTransaction
  | CrossChainTransaction;

export interface TransactionsByAccount {
  [address: string]: TransactionsByChain | undefined;
}

export interface TransactionsByChain {
  [chainId: number]: Transaction[] | undefined;
}

interface TransactionCommonFields {
  sellToken: TokenInfo & {
    name: string;
    symbol: string;
    decimals: number;
  };
  sellAmount: string;
  referrerId: string | undefined;
  sellAmountUsd?: number;
  sellTokenUsdPrice: number;
  buyToken: TokenInfo & {
    name: string;
    symbol: string;
    decimals: number;
  };
  buyAmount: string;
  buyAmountUsd?: number;
  buyTokenUsdPrice: number;
  hash: ChainHash;
  /**
   * The pre-reprice hash, set only when a transaction is sped up in the wallet
   * and its tracked `hash` is updated to the replacing transaction. Preserved
   * for traceability and as the stable key for analytics idempotency across the
   * hash change.
   */
  originalHash?: ChainHash;
  /** Set once terminal analytics have fired; written only by `claimTerminalAnalytics` so it syncs across tabs. */
  analyticsEmitted?: boolean;
  timestamp: string;
  feeSource: Fee | null;
  parsedTx?: ParsedSwap | null;
  /** The recipient address for swap-and-send transactions (undefined means sent to self) */
  recipient?: string;
  /**
   * Effective slippage tolerance (basis points) applied to the trade at submit
   * time. Standard EVM: the resolved slippage setting. Solana: the quote's max
   * (dynamic) slippage. Gasless: the Auto value when Auto is active, else
   * undefined (relayer default). Undefined for wraps. Read back by the updaters
   * for per-trade analytics.
   */
  slippageBps?: number;
  /**
   * How this trade was executed on-chain. `eip7702` means it was submitted via
   * the Alchemy 7702 client, yielding an ERC-4337-shaped receipt whose `from` is
   * the bundler, not the taker. Read by the updater to parse under the taker EOA.
   * Undefined is treated as `plain-eoa`.
   */
  executionMode?: ExecutionPlan['kind'];
  /** Slippage mode at submit; undefined for Solana, wraps, and non-Auto gasless. */
  slippageMode?: 'auto' | 'manual';
  /**
   * The 0x quote identifier (zid) of the quote executed by this transaction.
   * Read back by the updaters for per-trade analytics. Set at submit from the
   * quote for single-chain EVM lanes; cross-chain quotes carry no zid, so the
   * watcher fills it from the status response. Undefined for Solana (no 0x
   * zid) and limit orders.
   */
  zid?: string;
}

export type CurrentTransactionInfo = {
  tx: Transaction;
  address: string;
  chainId: number;
  isSuccessful: boolean;
  isFailed: boolean;
};

export function isSerializableTransactionReceipt(
  arg: unknown,
): arg is SerializableTransactionReceipt {
  if (!arg || typeof arg !== 'object') return false;

  const requiredKeys: (keyof SerializableTransactionReceipt)[] = [
    'to',
    'from',
    'type',
    'status',
    'gasUsed',
    'blockHash',
    'blockNumber',
    'contractAddress',
    'transactionHash',
    'transactionIndex',
    'cumulativeGasUsed',
    'effectiveGasPrice',
  ];

  return requiredKeys.every((key) => key in arg);
}

export function isGaslessReceipt(arg: unknown): arg is GaslessSubmitReceipt {
  if (!arg || typeof arg !== 'object') return false;

  const requiredKeys: (keyof GaslessSubmitReceipt)[] = [
    'type',
    'status',
    'tradeHash',
    'transactions',
  ];

  return requiredKeys.every((key) => key in arg);
}

export function isNotObservedReceipt(arg: unknown): arg is NotObservedReceipt {
  if (!arg || typeof arg !== 'object') return false;

  const requiredKeys: (keyof NotObservedReceipt)[] = ['status', 'detectedAt'];

  if (!requiredKeys.every((key) => key in arg)) return false;

  const candidate = arg as NotObservedReceipt;
  return candidate.status === 'not_observed' && typeof candidate.detectedAt === 'string';
}

export function isWalletCallFallbackReceipt(arg: unknown): arg is WalletCallFallbackReceipt {
  if (!arg || typeof arg !== 'object') return false;

  const candidate = arg as WalletCallFallbackReceipt;
  return (
    candidate.source === 'wallet_call' &&
    (candidate.status === 'success' || candidate.status === 'reverted')
  );
}

export function isReplacedReceipt(arg: unknown): arg is ReplacedReceipt {
  if (!arg || typeof arg !== 'object') return false;

  const candidate = arg as ReplacedReceipt;
  return (
    candidate.status === 'replaced' &&
    (candidate.reason === 'cancelled' || candidate.reason === 'replaced') &&
    typeof candidate.detectedAt === 'string'
  );
}
